import { makeAutoObservable, runInAction } from 'mobx';

export interface LocalNoteAttachment {
  id: string;
  noteId: string;
  noteType: string;
  fileName: string;
  contentType: string;
  sizeBytes: number;
  createdAt: string;
  orgId?: string;
  syncVersion?: number;
  isDeleted?: boolean;
}

export class NoteAttachmentStore {
  private attachments = new Map<string, LocalNoteAttachment>();
  private _noteIndex = new Map<string, Set<string>>();

  constructor() {
    makeAutoObservable(this);
  }

  getByNoteId(noteId: string): LocalNoteAttachment[] {
    const ids = this._noteIndex.get(noteId);
    if (!ids || ids.size === 0) return [];
    const result: LocalNoteAttachment[] = [];
    for (const id of ids) {
      const a = this.attachments.get(id);
      if (a && !a.isDeleted) result.push(a);
    }
    return result;
  }

  getCountByNoteId(noteId: string): number {
    return this.getByNoteId(noteId).length;
  }

  loadFromRecords(records: Record<string, unknown>[]): void {
    runInAction(() => {
      for (const r of records) {
        this._set(r.id as string, r as unknown as LocalNoteAttachment);
      }
    });
  }

  applyChanges(changes: { id: string; data?: Record<string, unknown>; isDeleted: boolean }[]): void {
    runInAction(() => {
      for (const c of changes) {
        if (c.isDeleted) this._delete(c.id);
        else if (c.data) this._set(c.id, c.data as unknown as LocalNoteAttachment);
      }
    });
  }

  removeFromMap(id: string): void {
    this._delete(id);
  }

  clear(): void {
    this.attachments.clear();
    this._noteIndex.clear();
  }

  private _set(id: string, attachment: LocalNoteAttachment): void {
    const existing = this.attachments.get(id);
    if (existing) {
      this._noteIndex.get(existing.noteId)?.delete(id);
    }
    this.attachments.set(id, attachment);
    if (!this._noteIndex.has(attachment.noteId)) {
      this._noteIndex.set(attachment.noteId, new Set());
    }
    this._noteIndex.get(attachment.noteId)!.add(id);
  }

  private _delete(id: string): void {
    const existing = this.attachments.get(id);
    if (existing) {
      this._noteIndex.get(existing.noteId)?.delete(id);
    }
    this.attachments.delete(id);
  }
}
